import React, { useEffect, useMemo, useState } from 'react';
import { getAchievements } from '../api';

function AchievementModal({ achievement, onClose }) {
  if (!achievement) return null;

  return (
    <div className="achievement-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="achievement-modal"
        role="dialog"
        aria-labelledby="achievement-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="achievement-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <div className="achievement-modal-badge" aria-hidden="true">
          <span className="achievement-icon-large">{achievement.icon}</span>
        </div>
        <h3 id="achievement-modal-title">{achievement.name}</h3>
        <p className="achievement-modal-saying">“{achievement.saying}”</p>
        <p className="achievement-modal-description">{achievement.description}</p>
        {achievement.earnedAt ? (
          <p className="achievement-modal-earned">
            Unlocked {new Date(achievement.earnedAt).toLocaleDateString()}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function AchievementGrid({ userId }) {
  const [data, setData] = useState({ total: 101, earnedCount: 0, earned: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  const timeZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    []
  );

  useEffect(() => {
    setLoading(true);
    getAchievements(userId, timeZone)
      .then((result) => {
        setData({
          total: result.total || 101,
          earnedCount: result.earnedCount || 0,
          earned: result.earned || [],
        });
      })
      .catch((err) => {
        setError(err.error || 'Unable to load achievements');
      })
      .finally(() => setLoading(false));
  }, [userId, timeZone]);

  const lockedCount = Math.max(0, (data.total || 101) - (data.earnedCount || 0));

  if (loading) {
    return <p className="field-note">Loading achievements…</p>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <section className="achievements-section" aria-label="Achievements">
      <div className="achievements-header">
        <h2>Achievements</h2>
        <p className="achievements-count">
          {data.earnedCount} / {data.total} unlocked
        </p>
      </div>
      <p className="field-note">
        Unearned achievements stay secret. Keep logging sessions to discover them.
      </p>

      {data.earnedCount > 0 ? (
        <div className="achievement-grid earned-grid">
          {data.earned.map((achievement) => (
            <button
              key={achievement.id}
              type="button"
              className="achievement-card earned"
              onClick={() => setSelected(achievement)}
              title={achievement.name}
            >
              <span className="achievement-badge" aria-hidden="true">
                <span className="achievement-icon">{achievement.icon}</span>
              </span>
              <span className="achievement-saying">{achievement.saying}</span>
            </button>
          ))}
        </div>
      ) : (
        <p className="field-note">No achievements unlocked yet. Log your first session to begin.</p>
      )}

      {lockedCount > 0 ? (
        <div className="achievement-grid locked-grid" aria-label="Locked achievements">
          {Array.from({ length: Math.min(lockedCount, 24) }, (_, index) => (
            <div key={`locked-${index}`} className="achievement-card locked" aria-hidden="true">
              <span className="achievement-badge locked-badge">
                <span className="achievement-icon">?</span>
              </span>
              <span className="achievement-saying locked-label">???</span>
            </div>
          ))}
          {lockedCount > 24 ? (
            <p className="field-note locked-more">+ {lockedCount - 24} more hidden</p>
          ) : null}
        </div>
      ) : null}

      <AchievementModal achievement={selected} onClose={() => setSelected(null)} />
    </section>
  );
}

export default AchievementGrid;
